import { isEmpty, isNone } from '@ember/utils';
import { RANDOM_COHORT_ALLOCATION_DIVISOR } from 'cio/utils/visual-workflow-builder';
import { Edge, EdgeType, ParsedTreeBranchNode, ParsedTreeBranchNodeBranch } from 'cio/models/campaign';
import ActionModel, { ExitType } from 'cio/models/action';
import merge from 'lodash.merge';

export const BRANCH_TYPES = [
  'conditional_branch_action',
  'multi_split_branch_action',
  'multi_lang_branch_action',
  'random_cohort_branch_action',
  'conditional_wait_action',
];

export function formatRandomCohortPathValue(value: number | undefined | null, trimTrailingZero = false) {
  if (value == null) {
    return '';
  }

  let formattedValue = (value / RANDOM_COHORT_ALLOCATION_DIVISOR).toFixed(1);

  if (trimTrailingZero) {
    formattedValue = formattedValue.replace('.0', '');
  }

  return formattedValue;
}

export function isSameEdge(edge1: Edge, edge2: Edge) {
  return edge1.to === edge2.to && edge1.from === edge2.from && edge1.type === edge2.type && edge1.index === edge2.index;
}

export function getActionsWithoutIncomingEdges(edges: Edge[], actions: ActionModel[]) {
  let targets = edges.map((edge) => edge.to).uniq();
  return actions.filter((action) => !targets.includes(action.id));
}

export function getNodeIdsWithoutIncomingEdges(edges: Edge[], nodeIds: string[]) {
  let targets = edges.map((edge) => edge.to).uniq();
  return nodeIds.filter((id) => !targets.includes(id));
}

export function arrayContainsEdge(edges: Edge[], edge: Edge) {
  return edges.findIndex((e) => isSameEdge(e, edge)) !== -1;
}

// Breadth-first traversal of graph starting at given edge
export function edgeBFS(
  targetId: string,
  otherTraversedBranchNodes: Record<string, boolean> = {},
  {
    targetIds,
    actionMap,
  }: {
    targetIds: Record<string, string[]>;
    actionMap: Record<string, ActionModel>;
  },
): [Record<string, boolean>, ActionModel | null] {
  let traversedNodes: Record<string, boolean> = {};
  let queue = [];

  let node = actionMap[targetId];

  if (!node) {
    return [traversedNodes, null];
  }

  traversedNodes[node.id] = true;
  queue.push(node);

  let rejoinNode = null;

  while (queue.length > 0) {
    let node = queue.shift() as ActionModel;

    if (otherTraversedBranchNodes[node.id] && !rejoinNode) {
      rejoinNode = actionMap[node.id];
    }

    let targets = targetIds[node.id] ?? [];

    targets.forEach((id) => {
      if (traversedNodes[id] == null) {
        let newNode = actionMap[id];

        if (!newNode) {
          return;
        }

        traversedNodes[id] = true;
        queue.push(newNode);
      }
    });
  }

  return [traversedNodes, rejoinNode];
}

export function sortEdges(edges: Edge[]) {
  return [...edges].sort((a, b) => {
    if (a.index && b.index) {
      return a.index > b.index ? 1 : -1;
    }

    if (a.index) {
      return 1;
    }

    if (b.index) {
      return -1;
    }

    return 0;
  });
}

// Using breadth-first traversal, finds a node's (a branch's) rejoin points
export function findRejoinPoints(
  node: ActionModel,
  { actionMap, targetIds }: { actionMap: Record<string, ActionModel>; targetIds: Record<string, string[]> },
) {
  let targets = targetIds[node.id];

  let rejoinPoints: ActionModel[] = [];
  let traversedNodes: Record<string, boolean> = {};
  targets.forEach((id) => {
    let bfs = edgeBFS(id, traversedNodes, { targetIds, actionMap });
    traversedNodes = merge({}, traversedNodes, bfs[0] ?? {});

    if (bfs[1]) {
      rejoinPoints.push(bfs[1]);
    }
    rejoinPoints = rejoinPoints.compact().uniq();
  });

  return rejoinPoints;
}

export function traverseBranchUntilRejoin(
  node: ActionModel,
  edge: Edge | null,
  tree: ParsedTreeBranchNode[] = [],
  traversedNodesMap: Record<string, boolean> = {},
  traversedEdges: Edge[] = [],
  rejoinPoints: ActionModel[] = [],
  branchRejoinPointsMap: Record<string, string> = {},
  {
    actions,
    edges,
    actionMap,
    targetIds,
    previousBranch,
    parentBranchMap = {},
    branches = {},
    nodesTraversedForBranch = [],
    allTraversedActions = [],
    depths = {},
    currentDepth = 0,
    parentBranchChains = {},
  }: {
    actions: ActionModel[];
    edges: Edge[];
    actionMap: Record<string, ActionModel>;
    targetIds: Record<string, string[]>;
    previousBranch: ActionModel | null;
    parentBranchMap: Record<string, string>;
    branches: Record<string, ParsedTreeBranchNode>;
    nodesTraversedForBranch: ActionModel[];
    allTraversedActions: ParsedTreeBranchNode[];
    depths: Record<string, number>;
    currentDepth: number;
    parentBranchChains: Record<string, string[]>;
  },
): ParsedTreeBranchNode[] {
  if (traversedNodesMap[node.id]) {
    return tree;
  }

  if (!parentBranchChains[node.id]) {
    parentBranchChains[node.id] = [];
  }

  if (previousBranch && !parentBranchChains[node.id].includes(previousBranch.id)) {
    let previousBranchChains = parentBranchChains[previousBranch.id] ?? [];
    parentBranchChains[node.id].push(...previousBranchChains, previousBranch.id);
  }

  tree = [...tree];
  depths[node.id] = currentDepth;

  if (BRANCH_TYPES.includes(node.type)) {
    let branchEdges = sortEdges(edges.filter((edge) => edge.from === node.id));

    let branchRejoinPoints: ActionModel[] = [];

    if (branchEdges.length === 1) {
      branchRejoinPoints = [actionMap[branchEdges[0].to]];
    } else {
      branchRejoinPoints = findRejoinPoints(node, { actionMap, targetIds });
    }

    if (branchRejoinPoints.length > 0) {
      branchRejoinPointsMap[node.id] = branchRejoinPoints[0].id;
    }

    traversedEdges.push(...branchEdges);
    let newRejoinPoints = [...rejoinPoints];
    newRejoinPoints.pushObjects(branchRejoinPoints);

    if (previousBranch) {
      parentBranchMap[node.id] = previousBranch.id;
    }

    nodesTraversedForBranch.push(node);

    let branch: ParsedTreeBranchNode = {
      type: 'branch',
      action: node,
      id: node.id,
      edge: edge as Edge,
      edges: branchEdges.map((edge) => {
        let allEdgeNodes: ActionModel[] = [];
        let nextNode = actionMap[edge.to];
        let nodes = newRejoinPoints.some((rp) => rp.id === edge.to)
          ? []
          : traverseBranchUntilRejoin(
              nextNode,
              edge,
              [],
              traversedNodesMap,
              traversedEdges,
              newRejoinPoints,
              branchRejoinPointsMap,
              {
                actions,
                edges,
                actionMap,
                targetIds,
                previousBranch: node,
                parentBranchMap,
                branches,
                nodesTraversedForBranch: allEdgeNodes,
                allTraversedActions,
                depths,
                currentDepth: currentDepth + 1,
                parentBranchChains,
              },
            );

        nodesTraversedForBranch.push(...allEdgeNodes);

        return {
          edge,
          nodes,
          allEdgeNodes,
        };
      }),
    };

    tree.push(branch);
    allTraversedActions.push(branch);
    branches[node.id] = branch;
    traversedNodesMap[node.id] = true;

    branchRejoinPoints.forEach((brp) => {
      if (!rejoinPoints.some((rp) => rp.id === brp.id)) {
        let subtree = traverseBranchUntilRejoin(
          brp,
          null,
          [],
          traversedNodesMap,
          traversedEdges,
          newRejoinPoints,
          branchRejoinPointsMap,
          {
            actions,
            edges,
            actionMap,
            targetIds,
            parentBranchMap,
            branches,
            previousBranch,
            nodesTraversedForBranch,
            allTraversedActions,
            depths,
            currentDepth,
            parentBranchChains,
          },
        );
        tree.push(...subtree);
      }
    });

    return tree;
  }

  let item: ParsedTreeBranchNode = {
    type: 'action',
    id: node.id,
    action: node,
    edge: edge as Edge,
  };
  tree.push(item);
  allTraversedActions.push(item);
  traversedNodesMap[node.id] = true;
  if (previousBranch) {
    nodesTraversedForBranch.push(node);
    parentBranchMap[node.id] = previousBranch.id;
  }

  let nextEdge = edges.find((edge) => edge.from === node.id);

  if (nextEdge) {
    let nextNode = actionMap[nextEdge.to];
    traversedEdges.push(nextEdge);

    if (!nextNode) {
      return tree;
    }

    if (rejoinPoints.some((point) => nextNode && point.id === nextNode.id)) {
      return tree;
    }

    return traverseBranchUntilRejoin(
      nextNode,
      nextEdge,
      tree,
      traversedNodesMap,
      traversedEdges,
      rejoinPoints,
      branchRejoinPointsMap,
      {
        actions,
        edges,
        actionMap,
        targetIds,
        previousBranch,
        parentBranchMap,
        branches,
        nodesTraversedForBranch,
        allTraversedActions,
        depths,
        currentDepth,
        parentBranchChains,
      },
    );
  }

  return tree;
}

export function getLastEdgeExits(edge: {
  edge: Edge;
  nodes: ParsedTreeBranchNode[];
  allEdgeNodes: ActionModel[];
}): ParsedTreeBranchNode[] {
  if (edge.nodes.length === 0) {
    return [];
  }

  let lastNode = edge.nodes[edge.nodes.length - 1];

  if (lastNode.action.type === 'exit_action') {
    return [lastNode];
  } else if (lastNode.type === 'branch') {
    return lastNode.edges.reduce<ParsedTreeBranchNode[]>((acc, edge) => {
      let exits = getLastEdgeExits(edge);
      acc.push(...exits);
      return acc;
    }, []);
  } else {
    return [];
  }
}

export function getLastBranchExits(branch: ParsedTreeBranchNodeBranch) {
  return branch.edges.reduce<ParsedTreeBranchNode[]>((acc, edge) => {
    let lastExits = getLastEdgeExits(edge);
    acc.push(...lastExits);
    return acc;
  }, []);
}

export function edgeEndsInExit(edge: {
  edge: Edge;
  nodes: ParsedTreeBranchNode[];
  allEdgeNodes: ActionModel[];
}): boolean {
  if (edge.nodes.length === 0) {
    return false;
  }

  let lastNode = edge.nodes[edge.nodes.length - 1];

  if (lastNode.action.type === 'exit_action') {
    return true;
  } else if (lastNode.type === 'branch') {
    return lastNode.edges.every((edge) => edgeEndsInExit(edge));
  } else {
    return false;
  }
}

export function edgesEndingInExitCount(branch: ParsedTreeBranchNodeBranch) {
  return branch.edges.filter((edge) => {
    return edgeEndsInExit(edge);
  }).length;
}

// Given an edge and a target node id, will traverse the graph until that node
// is hit. Returns a list of all traversed edges, node ids, any leaf nodes
// encountered (exits) and the last edges that connect to the target node id.
export function traverseUntil(
  edge: Edge,
  traverseUntilId: string | null,
  {
    edges = [],
    traversedEdges = [],
    traversedNodes = [],
    lastEdges = [],
    leafNodes = [],
  }: {
    edges: Edge[];
    traversedEdges?: Edge[];
    traversedNodes?: string[];
    lastEdges?: Edge[];
    leafNodes?: ParsedTreeBranchNode[];
  },
) {
  traversedEdges.push(edge);

  if (edge.to === traverseUntilId) {
    return {
      lastEdges: [...lastEdges, edge],
      traversedEdges,
      traversedNodes,
      leafNodes,
    };
  }

  let nextNodeId = edge.to;
  traversedNodes.push(nextNodeId);

  let nextEdges = edges.filter((e) => e.from === nextNodeId);

  // If the next node doesn't have any outgoing edges, that means it's an exit
  // (a leaf node). We should return at this point.
  if (nextEdges.length === 0) {
    return {
      lastEdges,
      traversedEdges,
      traversedNodes,
      leafNodes: [...leafNodes, { id: nextNodeId, edge }],
    };
  }

  nextEdges.forEach((e) => {
    let result = traverseUntil(e, traverseUntilId, {
      edges: edges.filter((e) => !traversedEdges.map((tn) => tn.to).includes(e.from)),
    });
    traversedEdges = [...traversedEdges, ...result.traversedEdges];
    traversedNodes = [...traversedNodes, ...result.traversedNodes];

    let uniqueLastEdges = result.lastEdges.filter((edge1) => {
      return !lastEdges.find((edge2) => isSameEdge(edge1, edge2));
    });

    lastEdges = [...lastEdges, ...uniqueLastEdges];
    leafNodes = [...leafNodes, ...result.leafNodes] as ParsedTreeBranchNode[];
  });

  return {
    lastEdges,
    traversedEdges,
    traversedNodes,
    leafNodes,
  };
}

// For a given node, returns all edges that leave it.
// For a normal action node, this will simply return its outgoing edge.
// For a branch node, it will return all edges that connect to the branch's
// reconnect point (which needs to be provided here).
export function retrieveOutgoingEdges(
  node: ActionModel,
  { edges = [], reconnectNodeId = null }: { edges: Edge[]; reconnectNodeId: string | null | undefined },
) {
  let edgesLeavingNode = edges.filter((edge) => edge.from === node.id);

  // Only branch nodes can have multiple outgoing edges. If this is a normal
  // node, return the edges right away.
  // Also, for pre branch migration wait untils, return early
  if (
    ![
      'conditional_branch_action',
      'multi_split_branch_action',
      'random_cohort_branch_action',
      'conditional_wait_action',
    ].includes(node.type) ||
    (node.type === 'conditional_wait_action' && node.isPreBranchWaitUntil)
  ) {
    return {
      outgoingEdges: edgesLeavingNode,
      traversedEdges: edgesLeavingNode,
      traversedNodes: [node],
      leafNodes: [],
    };
  }

  let outgoingEdges: Edge[] = [];
  let leafNodes: ParsedTreeBranchNode[] = [];
  let traversedNodes: string[] = [node.id];
  let traversedEdges: Edge[] = [];

  edgesLeavingNode.forEach((edge) => {
    let result = traverseUntil(edge, reconnectNodeId, { edges });
    outgoingEdges.push(...result.lastEdges);
    leafNodes.push(...(result.leafNodes as ParsedTreeBranchNode[]));
    traversedNodes.push(...result.traversedNodes);
    traversedEdges.push(...result.traversedEdges);
  });

  return {
    outgoingEdges,
    traversedEdges,
    traversedNodes,
    leafNodes,
  };
}

// Given a node and a list of edges, removes the node from the graph and adjusts
// the edges accordingly.
// If an "edgesToUpdate" array is provided and it contains some of the edges
// that are being changed, those edges will be updated to reflect the new state
// of the graph. "edgesToUpdate" can be used to maintain the right list of
// "insertAtEdges" when doing workflow operations.
// For example, when performing a "move" to a target "insertAtEdges", we first
// run this remove function, and afterwards a separate "add" action, so it's
// necessary to maintain legal "insertAtEdges".
export function removeNodeFromGraph(
  node: ActionModel,
  edges: Edge[],
  edgesToUpdate: Edge[] = [],
  nodeRejoinPoint: string | null | undefined = null,
) {
  // clone edges
  edges = edges.map((edge) => ({ ...edge }));

  let edgesIncomingToNode: Edge[] = [];
  let result = retrieveOutgoingEdges(node, {
    edges,
    reconnectNodeId: nodeRejoinPoint,
  });
  let edgesLeavingNode = result.outgoingEdges;
  let remainingEdges: Edge[] = [];

  // This will be non-empty only for branch actions
  let edgesWithinNode = result.traversedEdges.filter((edge) => !edgesLeavingNode.some((e) => isSameEdge(e, edge)));

  edges.forEach((edge) => {
    if (edge.to === node.id) {
      edgesIncomingToNode.push(edge);
    } else if (edgesLeavingNode.some((e) => isSameEdge(e, edge))) {
      // skip
    } else if (edgesWithinNode.some((e) => isSameEdge(e, edge))) {
      // If we're deleting a branch action, all the internals will get deleted
      // as well, so we can ignore the internal edges
    } else {
      remainingEdges.push(edge);
    }
  });

  let mergedEdges: Edge[] = [];

  // For a branch, this will be the same as the rejoin node's id. For a normal
  // action, there's only one leaving edge, so we get its id.
  let targetId = edgesLeavingNode[0].to;

  let newEdgesToUpdate: Edge[] = [];
  edgesToUpdate.forEach((edge) => {
    let isEdgeLeavingRemovedNode = edgesLeavingNode.some((e) => isSameEdge(e, edge));
    let isEdgeIncomingToRemovedNode = edgesIncomingToNode.some((e) => isSameEdge(e, edge));
    if (isEdgeLeavingRemovedNode) {
      // If one of the edges that need updating is an edge that is leaving the
      // node, then we need to create a new reference edge that will point to
      // the next action.
      // This happens when moving an action from the end of a path into the
      // reconnect point of that path's branch action. Because the moved action
      // is at the end of the path, its edge is pointing to that reconnect
      // point, and this is the edge here that needs adjusting.
      // The same can happen when moving a branch, but instead we have to update
      // multiple edges.
      edgesIncomingToNode.forEach((incomingEdge) => {
        let adjustedEdge = { ...incomingEdge, to: targetId };
        let alreadyHasAdjustedEdge = newEdgesToUpdate.some((e) => isSameEdge(e, adjustedEdge));
        if (!alreadyHasAdjustedEdge) {
          // If we're moving a branch from the path end to the reconnect point,
          // "edgesToUpdate" will include all the moved branch's outgoing edges.
          // After adjustments, those edges end up being one edge, and we only
          // want to keep one of them (no duplicate edges).
          newEdgesToUpdate.push(adjustedEdge);
        }
      });
    } else if (isEdgeIncomingToRemovedNode) {
      // Similarly to the previous case, we need to make adjustments if moving
      // an action from a reconnect point into the last spot of a branch's path.
      newEdgesToUpdate.push({ ...edge, to: targetId });
    } else {
      newEdgesToUpdate.push(edge);
    }
  });

  // If the node to remove is a branch action, the internals will also get
  // removed, so we can simply connect the incoming edges with the target and
  // ignore the internal edges
  edgesIncomingToNode.forEach((edge) => {
    let mergedEdge = { ...edge, to: targetId };
    mergedEdges.push(mergedEdge);
  });

  return {
    edges: [...remainingEdges, ...mergedEdges],
    edgesLeavingNode,
    edgesWithinNode,
    edgesToUpdate: newEdgesToUpdate,
  };
}

export function prepareEdgesForNode({ node, targetId }: { node: ActionModel; targetId: string }): Edge[] {
  if (node.type === 'conditional_branch_action') {
    // For a conditional split return a true and false edge
    return [
      { from: node.id, to: targetId, index: 0, type: 'branch' },
      { from: node.id, to: targetId, index: 1, type: 'branch' },
    ];
  } else if (node.type === 'random_cohort_branch_action') {
    // For a random cohort split return 100/0 edges
    return [
      { from: node.id, to: targetId, index: 0, type: 'branch' },
      { from: node.id, to: targetId, index: 1, type: 'branch' },
    ];
  } else if (node.type === 'multi_split_branch_action' || node.type === 'multi_lang_branch_action') {
    // For a multi split return two empty edges, and the "All others" edge
    return [
      {
        from: node.id,
        to: targetId,
        index: 0,
        type: 'branch',
      },
      {
        from: node.id,
        to: targetId,
        index: 1,
        type: 'branch',
      },
      {
        from: node.id,
        to: targetId,
        index: 2,
        type: 'branch',
      },
    ];
  }
  if (node.type === 'conditional_wait_action' && !node.isPreBranchWaitUntil) {
    return [
      {
        from: node.id,
        to: targetId,
        index: 0,
        type: 'branch',
      },
    ];
  } else if (node.type === 'exit_action') {
    // Don't insert any new edges after an exit action
    return [];
  } else {
    // For normal actions, return a regular edge
    return [{ from: node.id, to: targetId, type: 'continue' }];
  }
}

export function prepareEdgesForExistingNode({ node, targetId }: { node: ActionModel; targetId: string }): Edge[] {
  if (node.type === 'random_cohort_branch_action') {
    return [
      ...node.cohorts.map(
        (_cohort, index) =>
          ({
            from: node.id,
            to: targetId,
            index,
            type: 'branch',
          }) as Edge,
      ),
    ];
  } else if (node.type === 'multi_split_branch_action') {
    return [
      ...node.multiConditions.map(
        (_condition, index) =>
          ({
            from: node.id,
            to: targetId,
            index,
            type: 'branch',
          }) as Edge,
      ),
      {
        from: node.id,
        to: targetId,
        index: node.multiConditions.length - 1,
        type: 'branch',
      },
    ];
  } else if (node.type === 'conditional_wait_action' && !node.isPreBranchWaitUntil) {
    let edges = node.multiConditions.map((_, index) => ({
      from: node.id,
      to: targetId,
      index,
      type: 'branch' as EdgeType,
    }));

    if (!isEmpty(node.delayEventAttr)) {
      edges.push({
        from: node.id,
        to: targetId,
        index: edges.length,
        type: 'branch' as EdgeType,
      });
    }

    if (node.exitType !== ExitType.Never) {
      edges.push({
        from: node.id,
        to: targetId,
        index: edges.length,
        type: 'branch' as EdgeType,
      });
    }

    if (edges.length === 0) {
      return prepareEdgesForNode({ node, targetId });
    }

    return edges;
  } else {
    return prepareEdgesForNode({ node, targetId });
  }
}

// Adds a node to the edges graph. "edgesToInsertAt" is an array of edges where
// the new node should be placed.
// For example, if "edgesToInsertAt" is [ { from: 1, to: 2 } ],
// this should return the following new edges:
// [ { from: 1, to: -1 }, { from: -1, to: 2 } ]
// We use -1 as a temporary ID for the new node, until the backend saves it and
// assigns it a proper ID.
export function addNodeToGraph({
  node,
  edgesToInsertAt,
  edges,
  outgoingEdges,
  existing = false,
}: {
  node: ActionModel;
  edgesToInsertAt: Edge[];
  edges: Edge[];
  outgoingEdges?: Edge[];
  existing?: boolean;
}) {
  // Filter out edgesToInsertAt from the new edge array
  let newEdges = edges.filter((edge) => !edgesToInsertAt.some((e) => isSameEdge(e, edge)));

  // Add edges pointing from the actions before the edges we're inserting at to
  // the new node.
  // If the edges we're inserting at doesn't have a "from", it means it's a fake
  // edge that we only used to add an action to an empty campaign.
  newEdges.push(
    ...edgesToInsertAt
      .filter((edge) => !isNone(edge.from))
      .map((edge) => ({
        ...edge,
        to: node.id,
      })),
  );

  // edgesToInsertAt will always point to the same action. For example, when
  // adding a new node before another action, we are inserting it at the edge
  // before.
  // When adding a new node after a branch, the edges we're inserting at are
  // that branch's outgoing edges, which all point to the same point (the
  // reconnect point).
  let targetId = edgesToInsertAt[0].to;
  if (outgoingEdges) {
    // If outgoing edges are already provided, we need to adjust them to point
    // to the new target
    newEdges.push(...outgoingEdges.map((edge) => ({ ...edge, to: targetId })));
  } else {
    let prepareEdgesMethod = existing ? prepareEdgesForExistingNode : prepareEdgesForNode;
    // If no edges were provided, we need to add new ones
    newEdges.push(...prepareEdgesMethod({ node, targetId }));
  }

  return newEdges;
}

export function addNodesToGraph({
  nodes,
  edgesToInsertAt,
  edges,
  existing = false,
}: {
  nodes: ActionModel[];
  edgesToInsertAt: Edge[];
  edges: Edge[];
  existing: boolean;
}) {
  if (nodes.length === 1) {
    return addNodeToGraph({ node: nodes[0], edgesToInsertAt, edges, existing });
  }
  // Filter out edgesToInsertAt from the new edge array
  let newEdges = edges.filter((edge) => !edgesToInsertAt.some((e) => isSameEdge(e, edge)));

  let [firstNode] = nodes;
  let lastNode = nodes[nodes.length - 1];

  // Add edges pointing from the actions before the edges we're inserting at to
  // the first node of the array.
  // If the edges we're inserting at doesn't have a "from", it means it's a fake
  // edge that we only used to add an action to an empty campaign.
  newEdges.push(
    ...edgesToInsertAt
      .filter((edge) => !isNone(edge.from))
      .map((edge) => ({
        ...edge,
        to: firstNode.id,
      })),
  );

  let prepareEdgesMethod = existing ? prepareEdgesForExistingNode : prepareEdgesForNode;

  // Insert edges connecting nodes to each other in sequence
  nodes.forEach((node, index) => {
    if (index === 0) {
      return;
    }

    let previousNode = nodes[index - 1];
    newEdges.push(...prepareEdgesMethod({ node: previousNode, targetId: node.id }));
  });

  // edgesToInsertAt will always point to the same action. For example, when
  // adding a new node before another action, we are inserting it at the edge
  // before.
  // When adding a new node after a branch, the edges we're inserting at are
  // that branch's outgoing edges, which all point to the same point (the
  // reconnect point).
  let targetId = edgesToInsertAt[0].to;

  // Add edge from the last node to the target
  newEdges.push(...prepareEdgesMethod({ node: lastNode, targetId }));

  return newEdges;
}
